import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { saveProductVariant, updateProductVariant, fetchProductVariants, deleteProductVariant } from '../service/productVariantService';
import { fetchBasicProducts } from '../service/productService';
import { getCurrencies} from '../service/commonService';
import { fetchDiscount } from '../service/discountService';

const ProductVariantManager = () => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [filter, setFilter] = useState("");
  const [products, setProducts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [discounts, setDiscounts] = useState([]);

  const [form, setForm] = useState({
    variantId: null,
    productId: "",
    variantName: "",
    sku: "",
    color: "",
    size: "",
    imageUrl: "",
    price: "",
    stockQuantity: "",
    isActive: true,
    currencyId: "",
    taxPercent: "",
     sgstPercent: "",
        cgstPercent: "",
    discount: null
  });

  // Fetch product variants
  const fetchVariants = async () => {
    setLoading(true);
    try {
      const res = await fetchProductVariants(); //axiosInstance.get("/api/product-variants");
      setVariants(res.data);
    } catch (err) {
      console.error("Error fetching product variants", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVariants();
      loadData();
  }, []);

      const loadData = async () => {
    const prodRes = await fetchBasicProducts(); //axiosInstance.get("/api/products");
    setProducts(prodRes.data);

    const currencyRes = await getCurrencies(); //axiosInstance.get("/api/currencies");
    setCurrencies(currencyRes.data.content);
        
             const discountRes = await fetchDiscount();
     setDiscounts(discountRes);
      };
    
  const handleOpen = (variant = null) => {
    if (variant) {
      setEditingVariant(variant);
      setForm(variant);
    } else {
      setEditingVariant(null);
      setForm({
        variantId: null,
        productId: "",
        variantName: "",
        sku: "",
        color: "",
        size: "",
        imageUrl: "",
        price: "",
        stockQuantity: "",
        isActive: true,
        currencyId: "",
        taxPercent: "",
         sgstPercent: "",
        cgstPercent: "",
           discount: null
      });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "discountId") {
      setForm((prev) => ({
        ...prev,
        discount: {
          ...prev.discount,
          [name]: value
        }
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    try {
    if (
    !form.productId ||
    !form.variantName ||
    !form.sku ||
    !form.price ||
    !form.stockQuantity ||
    !form.currencyId 
  ) {
    alert("Please fill in all required fields.");
    return;
  }
        
      if (editingVariant) {
        await  updateProductVariant(form.variantId, form); 
      } else {
        await saveProductVariant(form); 
      }
      fetchVariants();
      handleClose();
    } catch (err) {
      console.error("Error saving product variant", err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this?")) {
    try {
      await deleteProductVariant(id); 
      fetchVariants();
    } catch (err) {
      console.error("Error deleting variant", err);
    }
    }
  };

  const columns = [
    { field: "variantId", headerName: "ID", width: 70 },
    { field: "productName", headerName: "Product Name", width: 120 },
    { field: "variantName", headerName: "Variant Name", width: 150 },
    { field: "sku", headerName: "SKU", width: 150 },
    { field: "color", headerName: "Color", width: 100 },
    { field: "size", headerName: "Size", width: 100 },
    { field: "price", headerName: "Price", width: 120 },
    { field: "taxPercent", headerName: "Tax Percentage", width: 120 },
    { field: "stockQuantity", headerName: "Stock Qty", width: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      renderCell: (params) => (
        <Box>
          <Button
            size="small"
            onClick={() => handleOpen(params.row)}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            size="small"
            onClick={() => handleDelete(params.row.variantId)}
            color="error"
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Delete
          </Button>
          <Button
            size="small"
            onClick={() => alert(JSON.stringify(params.row, null, 2))}
            variant="outlined"
          >
            View
          </Button>
        </Box>
      ),
    },
  ];

  // Filtered data
  const filteredVariants = variants.filter((v) =>
    v.variantName?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <TextField
          label="Filter by Name"
          variant="outlined"
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <Button variant="contained" onClick={() => handleOpen()}>
          Add Variant
        </Button>
      </Box>

      <DataGrid
        rows={filteredVariants}
        columns={columns}
        getRowId={(row) => row.variantId}
        loading={loading}
        autoHeight
        pageSize={5}
        rowsPerPageOptions={[5, 10, 20]}
      />

      {/* Dialog for Add/Edit */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingVariant ? "Edit Product Variant" : "Add Product Variant"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Variant Name"
            name="variantName"
            value={form.variantName}
            onChange={handleChange}
            required
          />
          <TextField
            fullWidth
            margin="dense"
            label="SKU"
            name="sku"
            value={form.sku}
            onChange={handleChange}
            required
          />
          <TextField
            fullWidth
            margin="dense"
            label="Color"
            name="color"
            value={form.color}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Size"
            name="size"
            value={form.size}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Price"
            name="price"
            type="number"
            value={form.price}
            onChange={handleChange}
            required
          />
          <TextField
            fullWidth
            margin="dense"
            label="Stock Quantity"
            name="stockQuantity"
            type="number"
            value={form.stockQuantity}
            onChange={handleChange}
            required
          />
              
              
            <FormControl fullWidth  margin="dense">
            <InputLabel id="discount-label">Discount</InputLabel>
            <Select
              labelId="discount-label"
              id="discountId"
              name="discountId"
              value={form?.discount?.discountId || ""}
              onChange={handleChange}
              label="Discount">
              <MenuItem value="">
                <em>Select Discount</em>
              </MenuItem>
              {discounts.map((c) => (
                <MenuItem key={c.discountId} value={c.discountId}>
                  {c.percentage} - {c.amount}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="dense"
            label="Tax Percentage"
            name="taxPercent"
            type="number"
            value={form.taxPercent}
            onChange={handleChange}
            required
            />

           <TextField
                fullWidth
                margin="dense"
                label="Image URL"
                name="imageUrl"
                value={form.imageUrl}
                onChange={handleChange}
                />
              
          <TextField
            fullWidth
            margin="dense"
            label="SGST Percentage"
            name="sgstPercent"
            type="number"
            value={form.sgstPercent}
            onChange={handleChange}
            required
          />
              
           <TextField
            fullWidth
            margin="dense"
            label="CGST Percentage"
            name="cgstPercent"
            type="number"
            value={form.cgstPercent}
            onChange={handleChange}
            required
          />
        
          <FormControl fullWidth margin="dense">
            <InputLabel>Currency</InputLabel>
            <Select name="currencyId" value={form?.currencyId || ""} onChange={handleChange}>
              {currencies && currencies.map(c => <MenuItem key={c.currencyId} value={c.currencyId}>{c.code}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel>Product</InputLabel>
            <Select name="productId" value={form?.productId || ""} onChange={handleChange}>
              {products && products.map(c => <MenuItem key={c.productId} value={c.productId}>{c.supplierName} - {c.productName}</MenuItem>)}
            </Select>
          </FormControl>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductVariantManager;
